import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, auth, fmtDate } from '../finance/shared';
import Icon from '../../../components/dashboard/DashboardIcons';
import { PAYMENT_STATUS_LABELS } from './subscriptionUtils';

const PAYMENT_STATUS_STYLES = { paid: 'bg-emerald-50 text-emerald-700 border-emerald-200', partial: 'bg-amber-50 text-amber-700 border-amber-200', unpaid: 'bg-red-50 text-red-600 border-red-200' };
const PAYMENT_METHOD_LABELS = { cash: 'نقداً', bank_transfer: 'حوالة بنكية', card: 'بطاقة', online: 'دفع إلكتروني' };
const PAYMENT_SOURCE_LABELS = { manual: 'تسجيل يدوي', parent_receipt: 'إيصال ولي أمر مؤكَّد' };

export default function SubscriptionHistoryPanel({ playerId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    setData(null);
    axios.get(`${API}/players/${playerId}/subscription-history`, auth())
      .then((res) => { if (active) setData(res.data); })
      .catch(() => { if (active) setData(false); });
    return () => { active = false; };
  }, [playerId]);

  if (data === null) return <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>;
  if (data === false) return <p className="text-xs text-slate-400">تعذر تحميل سجل الاشتراكات</p>;

  const { cycles, payments } = data;

  return (
    <div className="space-y-5" data-testid="subscription-history-panel">
      <div>
        <h4 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-slate-700"><Icon name="calendar" className="h-4 w-4 text-teal-500" /> دورات الاشتراك</h4>
        {cycles.length === 0 ? (
          <p className="text-xs text-slate-400">لا توجد دورات اشتراك مسجَّلة بعد</p>
        ) : (
          <div className="relative space-y-3 pr-4">
            <div className="absolute right-[7px] top-2 bottom-2 w-px bg-slate-200" />
            {cycles.map((c) => (
              <div key={c.id} className="relative" data-testid={`subscription-cycle-${c.id}`}>
                <span className={`absolute right-0 top-2 h-3 w-3 -translate-x-1/2 rounded-full border-2 border-white ${c.is_current ? 'bg-teal-600' : 'bg-slate-300'}`} />
                <div className={`mr-4 rounded-2xl border p-3 ${c.is_current ? 'border-teal-200 bg-teal-50/50' : 'border-slate-100 bg-slate-50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-bold text-slate-800">
                      {c.subscription_start ? fmtDate(c.subscription_start) : '—'} → {c.subscription_end ? fmtDate(c.subscription_end) : '—'}
                    </div>
                    {c.is_current && <span className="rounded-full bg-teal-600 px-2 py-0.5 text-[10px] font-bold text-white">الدورة الحالية</span>}
                  </div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                    {c.subscription_package && <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-slate-600 ring-1 ring-slate-200">{c.subscription_package}</span>}
                    {c.subscription_amount != null && <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-slate-600 ring-1 ring-slate-200">القيمة: {c.subscription_amount} ر.س</span>}
                    {c.subscription_paid_amount != null && <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-slate-600 ring-1 ring-slate-200">المدفوع: {c.subscription_paid_amount} ر.س</span>}
                    {c.subscription_discount_value ? <span className="rounded-full bg-white px-2 py-0.5 font-semibold text-amber-600 ring-1 ring-amber-200">خصم: {c.subscription_discount_value}{c.subscription_discount_type === 'percentage' ? '%' : ' ر.س'}</span> : null}
                    {c.subscription_payment_status && (
                      <span className={`rounded-full border px-2 py-0.5 font-bold ${PAYMENT_STATUS_STYLES[c.subscription_payment_status] || 'bg-slate-100 text-slate-500 border-slate-200'}`}>
                        {PAYMENT_STATUS_LABELS[c.subscription_payment_status] || c.subscription_payment_status}
                      </span>
                    )}
                  </div>
                  {!c.is_current && c.closed_by_name && (
                    <div className="mt-1.5 text-[10px] text-slate-400">أُغلقت هذه الدورة بواسطة {c.closed_by_name} — {c.closed_at ? fmtDate(c.closed_at) : ''}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {payments.length > 0 && (
        <div>
          <h4 className="mb-3 flex items-center gap-1.5 text-sm font-bold text-slate-700"><Icon name="banknotes" className="h-4 w-4 text-teal-500" /> سجل الدفعات</h4>
          <div className="space-y-1.5" data-testid="subscription-payments-log">
            {payments.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-xs">
                <div>
                  <span className="font-bold text-slate-800">{p.amount} ر.س</span>
                  <span className="mx-1.5 text-slate-300">·</span>
                  <span className="text-slate-500">{PAYMENT_METHOD_LABELS[p.payment_method] || p.payment_method || '—'}</span>
                  <span className="mx-1.5 text-slate-300">·</span>
                  <span className="text-slate-400">{PAYMENT_SOURCE_LABELS[p.source] || p.source}</span>
                </div>
                <div className="text-left text-[11px] text-slate-400">
                  {p.created_by_name} — {p.created_at ? fmtDate(p.created_at) : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
